import React, { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler);

const PerformanceCardCanvas = forwardRef(({
  displayName,
  stats,
  accountValues,
  planAdherence,
  riskReward,
  accountValueMetrics,
  journalStreak,
}, ref) => {
  const totalPnl = parseFloat(stats?.total_pnl || 0);
  const winRate = parseFloat(stats?.win_percent || 0);
  const numTrades = parseInt(stats?.num_trades || 0);
  const numWins = parseInt(stats?.num_wins || 0);
  const largestWin = parseFloat(stats?.largest_win || 0);
  const totalFees = parseFloat(stats?.total_fees || 0);

  // TRADE-959: new headline metrics
  const planAdherencePct = planAdherence?.followed?.win_pct;
  const avgRR = riskReward?.avg_rr;
  const maxDrawdown = accountValueMetrics?.max_drawdown_pct;
  const currentStreak = journalStreak?.current_streak;

  const pnlColor = totalPnl >= 0 ? '#4ade80' : '#f87171';
  const wrColor = winRate >= 50 ? '#4ade80' : '#f87171';
  const planColor = planAdherencePct != null && planAdherencePct >= 50 ? '#4ade80' : '#f87171';
  const ddColor = '#f87171';

  // Prepare chart data — always show all 7 days
  const avList = accountValues?.accountvalues || [];
  const hasChart = avList.length > 0;
  const sparkValues = avList.map(av => parseFloat(av.accountvalue));
  const sparkLabels = avList.map(av => av.date);
  const sparkColor = hasChart && sparkValues[sparkValues.length - 1] >= sparkValues[0]
    ? '#4ade80' : '#f87171';

  const chartData = {
    labels: sparkLabels,
    datasets: [{
      data: sparkValues,
      borderColor: sparkColor,
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.3,
      fill: false,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#64748b',
          font: { size: 9 },
          maxRotation: 0,
          callback: function(value, index) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            const parts = label.split('-');
            return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
          },
        },
        grid: { display: false },
        border: { color: '#334155' },
      },
      y: {
        display: true,
        position: 'right',
        ticks: {
          color: '#64748b',
          font: { size: 9 },
          maxTicksLimit: 4,
          callback: function(value) {
            if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'k';
            return '$' + value.toFixed(0);
          },
        },
        grid: { color: '#1e293b' },
        border: { display: false },
      },
    },
  };

  const formatPnl = (val) => {
    const formatted = `$${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return val >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  return (
    <div
      ref={ref}
      style={{
        width: 600,
        height: 540,
        backgroundColor: '#0f172a',
        padding: 30,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ color: '#38bdf8', fontSize: 14, marginBottom: 2 }}>
        MyTradingTracker
      </div>
      <div style={{ color: '#ffffff', fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
        {displayName}
      </div>

      {/* Divider */}
      <div style={{ height: 2, backgroundColor: '#334155', marginBottom: 14 }} />

      {/* Stats Row 1 */}
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Total P&L</div>
          <div style={{ color: pnlColor, fontSize: 24, fontWeight: 700 }}>{formatPnl(totalPnl)}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Win Rate</div>
          <div style={{ color: wrColor, fontSize: 24, fontWeight: 700 }}>{winRate}%</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Total Trades</div>
          <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 700 }}>{numTrades}</div>
        </div>
      </div>

      {/* Stats Row 2 */}
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Wins</div>
          <div style={{ color: '#4ade80', fontSize: 24, fontWeight: 700 }}>{numWins}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Largest Win</div>
          <div style={{ color: '#4ade80', fontSize: 24, fontWeight: 700 }}>
            ${largestWin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Total Fees</div>
          <div style={{ color: '#fb923c', fontSize: 24, fontWeight: 700 }}>
            ${totalFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* TRADE-959: Stats Row 3 — new headline metrics from the Insights epic */}
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Plan Adherence</div>
          <div style={{ color: planColor, fontSize: 22, fontWeight: 700 }}>
            {planAdherencePct != null ? `${planAdherencePct}%` : '—'}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Avg R:R</div>
          <div style={{ color: '#ffffff', fontSize: 22, fontWeight: 700 }}>
            {avgRR != null ? Number(avgRR).toFixed(2) : '—'}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Max Drawdown</div>
          <div style={{ color: ddColor, fontSize: 22, fontWeight: 700 }}>
            {maxDrawdown != null ? `${maxDrawdown}%` : '—'}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 2 }}>Journal Streak</div>
          <div style={{ color: '#ffffff', fontSize: 22, fontWeight: 700 }}>
            {currentStreak != null ? `${currentStreak}d` : '—'}
          </div>
        </div>
      </div>

      {/* Sparkline */}
      {hasChart && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
            <div style={{ color: '#94a3b8', fontSize: 14 }}>Account Value (7d)</div>
            <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700 }}>
              ${sparkValues.length > 0
                ? sparkValues[sparkValues.length - 1].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '0.00'}
            </div>
          </div>
          <div style={{ backgroundColor: '#1e293b', borderRadius: 4, height: 110 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        position: 'absolute',
        bottom: 10,
        left: 30,
        color: '#64748b',
        fontSize: 12,
      }}>
        mytradingtracker.netlify.app
      </div>
    </div>
  );
});

export default PerformanceCardCanvas;
